import {Injectable, inject} from '@angular/core';
import {TrajetDto} from '../dto/trajet.dto';
import {StatutTrajetEnum} from '../../gestion-navette/onglets-gestion-navette/trajets/model/statut-trajet.enum';
import {DateUtilsService} from './date-utils.service';
import {FormulaireFiltrageTrajets} from '../../gestion-navette/onglets-gestion-navette/trajets/model/formulaire-filtrage-trajets';

/**
 * Service permettant de filtrer les trajets dans les écrans "Gestion navette" et "Réservation navette" en fonction des critères de filtrage.
 */
@Injectable({
	providedIn: 'root'
})
export class TrajetFiltrageService {
	// Injections
	private dateUtilsService = inject(DateUtilsService);

	constructor() {
	}

	/**
	 * Récupère tous les lieux de départ et d'arrivée différents à partir des trajets.
	 *
	 * @param trajets les trajets pour lesquels récupérer les lieux de départ et d'arrivée.
	 */
	recupererLieuxDepartArrivee(trajets: TrajetDto[]): string[] {
		return [
			...new Set(trajets.flatMap(t => [t.nomLieuDepart, t.nomLieuArrivee]))
		];
	}

	/**
	 * Filtre les trajets disponibles en fonction des critères de réservation fournis.
	 * Un trajet est retenu uniquement s'il respecte l'ensemble des filtres : statut disponible, lieux de départ/arrivée et plages horaires.
	 * Si certains filtres ne sont pas renseignés, ne fait pas de filtrage.
	 *
	 * @param {TrajetDto[]} trajets la liste des trajets à filtrer.
	 * @param {boolean} voirReservation indique si l'on souhaite voir les trajets déjà réservés par l'utilisateur connecté.
	 * @param {string[]} statuts les statuts de trajet autorisés.
	 * @param {string[]} lieuxDepart les lieux de départ autorisés.
	 * @param {string[]} lieuxArrivee les lieux d'arrivée autorisés.
	 * @param {string} heureDepart l'heure de départ minimale souhaitée.
	 * @param {string} heureArrivee l'heure d'arrivée maximale souhaitée.
	 * @return {TrajetDto[]} la liste des trajets correspondant à tous les critères.
	 */
	filtrerTrajetsParCriteresReservation(trajets: TrajetDto[], voirReservation: boolean, statuts: string[], lieuxDepart: string[], lieuxArrivee: string[],
			heureDepart: string, heureArrivee: string): TrajetDto[] {
		return trajets.filter(trajet => [
			() => this.respecteFiltreStatut(statuts, voirReservation, trajet),
			() => this.respecteFiltreLieuxDepart(lieuxDepart, trajet),
			() => this.respecteFiltreLieuxArrivee(lieuxArrivee, trajet),
			() => this.respecteFiltreHeureDepart(heureDepart, trajet),
			() => this.respecteFiltreHeureArrivee(heureArrivee, trajet)
		].every(check => check()));
	}

	/**
	 * Filtre les trajets en fonction des critères du formulaire de filtrage de la gestion navette.
	 * Un trajet est retenu uniquement s'il respecte l'ensemble des filtres actifs.
	 * Si un filtre n'est pas renseigné, il est ignoré.
	 *
	 * @param {TrajetDto[]} trajets la liste des trajets à filtrer.
	 * @param {FormulaireFiltrageTrajets} filtres les critères de filtrage à appliquer.
	 * @param {boolean} afficherTrajetsPasses indique si les trajets passés doivent être inclus.
	 * @return {TrajetDto[]} la liste des trajets correspondant à tous les critères actifs.
	 */
	filtrerTrajetsParCriteresGestionNavette(trajets: TrajetDto[], filtres: FormulaireFiltrageTrajets, afficherTrajetsPasses: boolean): TrajetDto[] {
		return trajets.filter(trajet => [
			() => !this.estTrajetPasse(afficherTrajetsPasses, trajet),
			() => this.respecteFiltreStatut(filtres.statutsTrajet, false, trajet),
			() => this.respecteFiltreLieuxDepart(filtres.lieuxDepart, trajet),
			() => this.respecteFiltreLieuxArrivee(filtres.lieuxArrivee, trajet),
			() => this.respecteFiltreHeureDepart(filtres.heureDepart, trajet),
			() => this.respecteFiltreHeureArrivee(filtres.heureArrivee, trajet),
			() => this.respecteFiltreMinimumPlaces(filtres.minimumPlaces, trajet),
			() => this.respecteFiltreMaximumPlaces(filtres.maximumPlaces, trajet),
		].every(check => check()));
	}

	/**
	 * Vérifie si un trajet est passé en fonction de la date de départ et du paramètre d'affichage des trajets passés.
	 *
	 * @param {boolean} afficherTrajetsPasses le paramètre indiquant si les trajets passés doivent être affichés ou non.
	 * @param {TrajetDto} trajet le trajet à vérifier.
	 * @return {boolean} true si le trajet est passé et ne doit pas être affiché, false sinon.
	 */
	private estTrajetPasse(afficherTrajetsPasses: boolean, trajet: TrajetDto): boolean {
		return !afficherTrajetsPasses && new Date(trajet.dateDepart) < new Date();
	}

	/**
	 * Vérifie si le trajet est déjà réservé par l'utilisateur connecté.
	 *
	 * @param {TrajetDto} trajet le trajet à vérifier.
	 * @return {boolean} true si le trajet est réservé par l'utilisateur connecté, false sinon.
	 */
	private estReserverParUtilisateurConnecte(trajet: TrajetDto): boolean {
		return !!trajet.reservationId;
	}

	/**
	 * Vérifie les trajets en fonction des statuts sélectionnés et de la volonté de voir les réservations de l'utilisateur connecté.
	 * Si aucun filtre n'est actif (aucun statut sélectionné et réservations non demandées), tous les trajets sont retenus.
	 * Si le filtre "trajets disponibles" est actif, les trajets déjà réservés par l'utilisateur connecté sont exclus de ce filtre
	 * (ils ne sont retenus que via le filtre "réservations" s'il est actif).
	 *
	 * @param {string[]} statutsTrajet les statuts sélectionnés pour le filtrage.
	 * @param {boolean} voirReservation indique si l'on souhaite voir les trajets déjà réservés par l'utilisateur connecté.
	 * @param {TrajetDto} trajet le trajet à vérifier.
	 * @return {boolean} true si le trajet respecte le filtre de statut et/ou de réservation, false sinon.
	 */
	private respecteFiltreStatut(statutsTrajet: string[], voirReservation: boolean, trajet: TrajetDto): boolean {
		const aucunFiltreActif = statutsTrajet?.length === 0 && !voirReservation;
		if (aucunFiltreActif) {
			return true;
		}

		const estReserve = this.estReserverParUtilisateurConnecte(trajet);
		const respecteFiltreStatutsSelectionnes = statutsTrajet?.length > 0 && statutsTrajet.includes(trajet.statut) && !estReserve;
		const respecteFiltreReservation = voirReservation && estReserve;

		return respecteFiltreStatutsSelectionnes || respecteFiltreReservation;
	}

	/**
	 * Vérifie les trajets en fonction des lieux de départ sélectionnés.
	 *
	 * @param {string[]} lieuxSelectionnes les lieux sélectionnés.
	 * @param {TrajetDto} trajet le trajet à vérifier.
	 * @return {boolean} true si le lieu de départ du trajet correspond aux lieux sélectionnés, false sinon.
	 */
	private respecteFiltreLieuxDepart(lieuxSelectionnes: string[], trajet: TrajetDto): boolean {
		return lieuxSelectionnes.length === 0 || lieuxSelectionnes.includes(trajet.nomLieuDepart);
	}

	/**
	 * Vérifie les trajets en fonction des lieux d'arrivée sélectionnés.
	 *
	 * @param {string[]} lieuxSelectionnes les lieux sélectionnés.
	 * @param {TrajetDto} trajet le trajet à vérifier.
	 * @return {boolean} true si le lieu d'arrivée du trajet correspond aux lieux sélectionnés, false sinon.
	 */
	private respecteFiltreLieuxArrivee(lieuxSelectionnes: string[], trajet: TrajetDto): boolean {
		return lieuxSelectionnes.length === 0 || lieuxSelectionnes.includes(trajet.nomLieuArrivee);
	}

	/**
	 * Vérifie les trajets en fonction de l'heure de départ sélectionnée.
	 *
	 * @param {string} heure l'heure de départ sélectionnée.
	 * @param {TrajetDto} trajet le trajet à vérifier.
	 * @return {boolean} true si l'heure de départ du trajet correspond à l'heure sélectionnée, false sinon.
	 */
	private respecteFiltreHeureDepart(heure: string, trajet: TrajetDto): boolean {
		return !heure || this.dateUtilsService.toMinutes(new Date(heure)) <= this.dateUtilsService.toMinutes(new Date(trajet.dateDepart));
	}

	/**
	 * Vérifie les trajets en fonction de l'heure d'arrivée sélectionnée.
	 *
	 * @param {string} heure l'heure d'arrivée sélectionnée.
	 * @param {TrajetDto} trajet le trajet à vérifier.
	 * @return {boolean} true si l'heure d'arrivée du trajet correspond à l'heure sélectionnée, false sinon.
	 */
	private respecteFiltreHeureArrivee(heure: string, trajet: TrajetDto): boolean {
		return !heure || this.dateUtilsService.toMinutes(new Date(heure)) >= this.dateUtilsService.toMinutes(new Date(trajet.dateArrivee));
	}

	/**
	 * Vérifie les trajets en fonction du nombre maximum de places sélectionné.
	 *
	 * @param {number} maximumPlaces le nombre maximum de places sélectionné.
	 * @param {TrajetDto} trajet le trajet à vérifier.
	 * @return {boolean} true si le nombre de places du trajet est inférieur ou égal au nombre maximum de places sélectionné, false sinon.
	 */
	private respecteFiltreMaximumPlaces(maximumPlaces: number, trajet: TrajetDto): boolean {
		return !maximumPlaces || trajet.nombrePlaces <= maximumPlaces;
	}

	/**
	 * Vérifie les trajets en fonction du nombre minimum de places sélectionné.
	 *
	 * @param {number} minimumPlaces le nombre minimum de places sélectionné.
	 * @param {TrajetDto} trajet le trajet à vérifier.
	 * @return {boolean} true si le nombre de places du trajet est supérieur ou égal au nombre minimum de places sélectionné, false sinon.
	 */
	private respecteFiltreMinimumPlaces(minimumPlaces: number, trajet: TrajetDto): boolean {
		return !minimumPlaces || trajet.nombrePlaces >= minimumPlaces;
	}
}
